import { requestGQL } from 'common/graphQlClient';
import { useQueryClient } from 'react-query';
import { useOfflineMutation } from 'common/offline/useOfflineMutation';
import { generateNodeId } from 'common/offline/generateNodeId';
import { NodeData } from 'app/connection/api';
import { gql } from 'graphql-request';

import { GetOutletsResponse } from '../api';

// Use the correct mutation format that includes tpOutletDetails
const CREATE_TPOUTLET_WITH_DETAILS = gql`
  mutation createTpOutlet($input: TpOutletInput!) {
    createTpOutlet(input: $input)
  }
`;

type CreateTPOutletPayload = {
  input: {
    propertyId: number;
    premiseId: number;
    tpOutletDetails: {
      id: string;
    };
  };
};

type OptimisticTPOutletData = {
  tpOutletDetails: {
    id: string;
  };
  propertyId: number;
  premiseId: number;
};

export const useCreateTPOutlet = (propertyId: null | number, premiseId: null | number) => {
  const queryClient = useQueryClient();

  // Optimistic update function to add TP outlet to cache immediately
  const applyOptimisticUpdate = (optimisticData: unknown) => {
    const data = optimisticData as OptimisticTPOutletData;
    const cacheKey = ['propertyConnection', propertyId];

    queryClient.setQueryData<GetOutletsResponse | undefined>(cacheKey, (oldData) => {
      if (!oldData?.propertyById?.connectionByConnectionId?.serialized2) {
        return oldData;
      }

      const newTpOutletNode: NodeData = {
        id: data.tpOutletDetails.id,
        text: null,
        model: 'tpOutlet',
        group: String(data.premiseId),
        cfObject: {
          images: [],
          location: undefined,
        },
      };

      return {
        ...oldData,
        propertyById: {
          ...oldData.propertyById,
          connectionByConnectionId: {
            ...oldData.propertyById.connectionByConnectionId,
            serialized2: {
              ...oldData.propertyById.connectionByConnectionId.serialized2,
              nodeDataArray: [
                ...oldData.propertyById.connectionByConnectionId.serialized2.nodeDataArray,
                newTpOutletNode,
              ],
            },
          },
        },
      };
    });
  };

  const mutation = useOfflineMutation<string, { message: string }, CreateTPOutletPayload>({
    mutationFn: async (payload) => requestGQL(CREATE_TPOUTLET_WITH_DETAILS, payload),
    mutationName: 'createTpOutlet',
    graphqlDocument: CREATE_TPOUTLET_WITH_DETAILS,
    entity: 'tpOutlet',
    operationType: 'CREATE',
    propertyId: propertyId ?? undefined,
    invalidateKeys: [['propertyConnection', propertyId]],
    getOptimisticData: (payload) => ({
      tpOutletDetails: payload.input.tpOutletDetails,
      propertyId: payload.input.propertyId,
      premiseId: payload.input.premiseId,
    }),
    applyOptimisticUpdate,
  });

  return {
    ...mutation,
    mutate: () => {
      const tpOutletId = generateNodeId();
      mutation.mutate({
        input: {
          propertyId: propertyId!,
          premiseId: premiseId!,
          tpOutletDetails: { id: tpOutletId },
        },
      });
    },
  };
};
